import React, { createContext, createRef, useCallback, useMemo } from "react";
import { Toast } from "primereact/toast";

type ToastSeverity = "success" | "info" | "warn" | "error";

const TOAST_LIFE: Record<ToastSeverity, number> = {
  success: 3000,
  info: 3000,
  warn: 60000,
  error: 8000,
};

type ToastPayload = {
  severity: ToastSeverity;
  message: string;
  summary?: string;
};

type MessageContextType = {
  pushMessageFromMutationResponse: (response: any) => void;
  pushMessageFromRequest: (response: any) => void;
  pushMessageForToast: (payload: ToastPayload) => void;
};

export const MessageContext = createContext<MessageContextType>(
  {} as MessageContextType
);

const toastRef = createRef<Toast>();

const MessageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const pushMessage = useCallback((data: any) => {
    if (!toastRef.current) return;

    // Handle direct toast payload
    if (data?.severity && data?.message) {
      toastRef.current.show({
        severity: data.severity,
        summary: data.summary || data.severity.toUpperCase(),
        detail: data.message,
        life: TOAST_LIFE[data.severity as ToastSeverity],
      });
      return;
    }

    // Handle API / mutation response
    if (data?.success) {
      toastRef.current.show({
        severity: "success",
        summary: "Success",
        detail: data.message || "Operation completed successfully",
        life: TOAST_LIFE.success,
      });
    } else {
      toastRef.current.show({
        severity: "error",
        summary: "Error",
        detail:
          data?.errors?.detail ||
          data?.errors?.message ||
          "Something went wrong",
        life: TOAST_LIFE.error,
      });
    }
  }, []);

  const pushMessageFromMutationResponse = useCallback(
    (response: any) => pushMessage(response),
    [pushMessage]
  );

  const pushMessageFromRequest = useCallback(
    (response: any) => pushMessage(response),
    [pushMessage]
  );

  const pushMessageForToast = useCallback(
    (payload: ToastPayload) => pushMessage(payload),
    [pushMessage]
  );

  const value = useMemo(
    () => ({
      pushMessageFromMutationResponse,
      pushMessageFromRequest,
      pushMessageForToast,
    }),
    [
      pushMessageFromMutationResponse,
      pushMessageFromRequest,
      pushMessageForToast,
    ]
  );

  return (
    <MessageContext.Provider value={value}>
      <Toast ref={toastRef} appendTo="self" baseZIndex={9999} />
      {children}
    </MessageContext.Provider>
  );
};

export default MessageProvider;
